/**
 * Single source of truth for video-file detection (audit #16).
 *
 * This list had drifted into two versions across ~14 files: an 8-entry
 * superset in the gallery/cart UI and a 5-entry subset in the SEO/feed-facing
 * code. The consequence: an `.m4v`/`.ogv`/`.3gp` PRIMARY product image was
 * treated as a video by the gallery but as an IMAGE by the Google/Meta/
 * Pinterest feeds + OG cards — i.e. a broken video file shipped as
 * `image_link` / `og:image`. Everything now imports from here.
 *
 * Server-safe (no React / DOM imports) so API routes, lib, and components can
 * all share it.
 */
export const VIDEO_EXTENSIONS = [
  "mp4",
  "webm",
  "mov",
  "avi",
  "mkv",
  "m4v",
  "ogv",
  "3gp",
] as const;

/** True when the key/path ends in a known video extension. */
export function isVideoKey(key: string | null | undefined): boolean {
  if (!key) return false;
  const ext = key.split(".").pop()?.toLowerCase();
  return !!ext && (VIDEO_EXTENSIONS as readonly string[]).includes(ext);
}

/**
 * Pick the primary STILL-image key from a product's media list, skipping any
 * videos. Returns `null` when the ONLY media is video.
 *
 * Callers MUST treat null as "no image": omit `image` from the Product JSON-LD
 * and use a static fallback for the rendered hero. They must NEVER fall back to
 * the raw video key — doing so breaks `next/image` (it can't decode a video)
 * and injects a non-image URL into the Product `image` array, which Google /
 * Merchant Center reject. (Guards the 2026-07 video-primary-image PDP bug.)
 */
export function pickPrimaryStillKey(
  keys: readonly (string | null | undefined)[]
): string | null {
  for (const k of keys) {
    if (k && !isVideoKey(k)) return k;
  }
  return null;
}
